import { getDomains } from '@/helpers/getDomains';
import type { HtmlEmbedHint } from '@heidi/evidence/contracts';

// `HtmlEmbedHint` is declared in `@heidi/evidence/contracts` — it describes an
// Evidence source, not a URL predicate. Re-exported here so the source-viewer
// and CPD readers of this module keep resolving through the path they use.
export type { HtmlEmbedHint };

const ML_SCRIBE_API_PATH_PATTERN = /\/api\/(v1|v2|v2-1|v2-2)\/ml-scribe\//;
const INTEGRATION_PARTNER_HTML_PATH_PATTERN =
  /(?:^|\/)api\/v2\/ml-scribe\/evidence\/integration-partners\/[^/]+\/[^/]+\/html\/?$/;
const URL_WITH_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
const URL_PARSE_BASE = 'https://source-viewer.local';
const AUTHENTICATED_SOURCE_TERMINAL_SEGMENTS = new Set(['html', 'read', 'download']);

function getPathname(url: string): string | undefined {
  try {
    return new URL(url, URL_PARSE_BASE).pathname;
  } catch {
    return undefined;
  }
}

function hasUrlScheme(url: string): boolean {
  return URL_WITH_SCHEME_PATTERN.test(url);
}

function isActiveScribeUrl(url: string): boolean {
  if (!hasUrlScheme(url)) return true;

  let targetUrl: URL;
  try {
    targetUrl = new URL(url);
  } catch {
    return false;
  }

  const domains = getDomains();
  const scribeBaseUrls = [
    domains.baseV1Url,
    domains.baseV2Url,
    domains.baseV2_1Url,
    domains.baseV2_2Url,
  ];

  return scribeBaseUrls.some((baseUrl) => {
    const parsedBaseUrl = new URL(baseUrl);
    return (
      targetUrl.origin === parsedBaseUrl.origin &&
      targetUrl.pathname.startsWith(parsedBaseUrl.pathname)
    );
  });
}

/**
 * Backend-hosted citation URLs require a bearer token fetch before rendering.
 * Direct browser navigation cannot work because it cannot attach the JWT.
 */
export function isAuthenticatedSourceUrl(url: string, embed?: HtmlEmbedHint): boolean {
  if (!isActiveScribeUrl(url)) return false;

  const pathname = getPathname(url);
  if (!pathname || !ML_SCRIBE_API_PATH_PATTERN.test(pathname)) return false;

  if (INTEGRATION_PARTNER_HTML_PATH_PATTERN.test(pathname)) return true;
  if (embed?.mode === 'html' && pathname.endsWith('/html')) return true;

  const terminalSegment = pathname.split('/').filter(Boolean).at(-1);
  return !!terminalSegment && AUTHENTICATED_SOURCE_TERMINAL_SEGMENTS.has(terminalSegment);
}

/** Resolve a backend-hosted source/read path against the active ml-scribe base URL. */
export function getAuthenticatedSourceUrl(url: string): string {
  if (hasUrlScheme(url)) return url;

  const parsedUrl = new URL(url, URL_PARSE_BASE);
  const match = ML_SCRIBE_API_PATH_PATTERN.exec(parsedUrl.pathname);
  if (!match) {
    return url;
  }

  const version = match[1];
  const domains = getDomains();
  const baseUrl =
    version === 'v1'
      ? domains.baseV1Url
      : version === 'v2-1'
        ? domains.baseV2_1Url
        : version === 'v2-2'
          ? domains.baseV2_2Url
          : domains.baseV2Url;
  const endpointPath = parsedUrl.pathname.slice(match.index + match[0].length);
  return `${baseUrl}${endpointPath}${parsedUrl.search}`;
}
